import { supabase } from '@/lib/supabase';
import type { ChecklistItemExample, ExampleWithUrl } from '@/types/checklistExamples';
import { finalPath, pendingPath } from '@/lib/exampleDraft';
import type { ExampleWriteRow } from '@/lib/exampleDraft';
import { ACCEPTED_EXAMPLE_TYPES, MAX_EXAMPLE_BYTES } from '@/lib/exampleAuthoringCopy';

/**
 * EPIC-EXP-001 · Reading authored example photos for a checklist field.
 *
 * ⚠️ RESOLUTION LIVES IN THE DATABASE, NOT HERE. `checklist_item_examples_for`
 * decides whether a field shows its library template's examples or its own,
 * because that decision depends on `checklist_items.source_library_*` which
 * this layer has no business knowing about. EXP-1.3's suite verifies the rule
 * live (8/8), including the case a client-side implementation would get wrong:
 * an item whose library template was deleted falls back to its own examples
 * rather than showing none.
 *
 * The function is SECURITY INVOKER, so RLS applies to the caller exactly as it
 * would to a direct query. There is no tenant filter to pass and none should
 * be added: a caller-supplied filter is not a filter.
 */
class ChecklistExampleService {
  private readonly STORAGE_BUCKET = 'checklist-examples';
  private readonly SIGNED_URL_TTL_SECONDS = 3600;

  /**
   * Examples a checklist field should show, in authored order.
   *
   * Returns an empty array rather than throwing when there are none, because
   * "no examples" is the normal case for most fields and the chip is simply
   * not rendered. A genuine failure is logged and also yields an empty array:
   * a field whose photo tiles work should not be taken down by guidance that
   * failed to load.
   */
  async getExamplesForItem(checklistItemId: number): Promise<ExampleWithUrl[]> {
    if (!Number.isFinite(checklistItemId)) return [];

    const { data, error } = await supabase.rpc('checklist_item_examples_for', {
      p_checklist_item_id: checklistItemId,
    });

    if (error) {
      console.error('[checklistExampleService] could not resolve examples', {
        checklistItemId,
        code: error.code,
        message: error.message,
      });
      return [];
    }

    const rows = (data ?? []) as ChecklistItemExample[];
    if (rows.length === 0) return [];

    return this.withSignedUrls(rows);
  }

  /**
   * Examples authored on a LIBRARY item, for the template editor.
   *
   * The resolver is for fields; the authoring screen edits one side directly,
   * and a library item has no `checklist_items` row to resolve through.
   */
  async getExamplesForLibraryItem(
    libraryTemplateId: string,
    libraryItemId: string,
  ): Promise<ExampleWithUrl[]> {
    const { data, error } = await supabase
      .from('checklist_item_examples')
      .select('*')
      .eq('library_template_id', libraryTemplateId)
      .eq('library_item_id', libraryItemId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[checklistExampleService] could not load library examples', {
        libraryTemplateId,
        libraryItemId,
        message: error.message,
      });
      return [];
    }

    const rows = (data ?? []) as ChecklistItemExample[];
    return rows.length === 0 ? [] : this.withSignedUrls(rows);
  }

  /** Examples authored directly on a pipeline item, for the template editor. */
  async getExamplesOwnedByItem(checklistItemId: number): Promise<ExampleWithUrl[]> {
    const { data, error } = await supabase
      .from('checklist_item_examples')
      .select('*')
      .eq('checklist_item_id', checklistItemId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[checklistExampleService] could not load item examples', {
        checklistItemId,
        message: error.message,
      });
      return [];
    }

    const rows = (data ?? []) as ChecklistItemExample[];
    return rows.length === 0 ? [] : this.withSignedUrls(rows);
  }

  /**
   * EXP-2.5 · upload one file to the PENDING prefix.
   *
   * ⚠️ NOTHING IS WRITTEN TO THE DATABASE HERE. Uploading is only what makes a
   * thumbnail appear; the row is written by Save item. That split is the whole
   * point of the pending path: a draft that is cancelled leaves files under
   * `{tenant}/pending/{draftId}/` and no rows anywhere, and those files are
   * removed by `discardPending` rather than swept later by a job whose query
   * might one day be wrong.
   */
  async uploadPending(params: {
    file: File;
    tenantId: string;
    draftId: string;
  }): Promise<{ storagePath: string; url: string } | null> {
    const { file, tenantId, draftId } = params;

    if (file.size > MAX_EXAMPLE_BYTES) {
      throw new Error(`${file.name} is larger than 10 MB`);
    }
    if (!ACCEPTED_EXAMPLE_TYPES.includes(file.type as (typeof ACCEPTED_EXAMPLE_TYPES)[number])) {
      throw new Error(`${file.name} is not a PNG or JPG`);
    }

    const extension = file.name.split('.').pop() ?? 'jpg';
    const objectName = `${Date.now()}_${crypto.randomUUID().split('-')[0]}.${extension}`;
    const storagePath = pendingPath(tenantId, draftId, objectName);

    const { error } = await supabase.storage
      .from(this.STORAGE_BUCKET)
      .upload(storagePath, file, { cacheControl: '3600', upsert: false });

    if (error) {
      console.error('[checklistExampleService] pending upload failed', {
        storagePath,
        message: error.message,
      });
      throw error;
    }

    const { data } = await supabase.storage
      .from(this.STORAGE_BUCKET)
      .createSignedUrl(storagePath, this.SIGNED_URL_TTL_SECONDS);

    return { storagePath, url: data?.signedUrl ?? '' };
  }

  /**
   * EXP-2.5 · Cancel. Remove the files this session put in the pending prefix.
   *
   * ⚠️ CALLERS MUST PASS `pendingPathsToDiscard(draft)` AND NOTHING ELSE. That
   * function deliberately excludes files belonging to already-saved examples,
   * because Cancel means "leave the item as it was" and deleting those would
   * make Cancel destructive.
   *
   * ⚠️ Failure is logged and swallowed. A cancel that throws would trap the
   * admin in a panel they asked to close, and the worst case of a failed
   * cleanup is an unreferenced file under a pending prefix.
   */
  async discardPending(storagePaths: string[]): Promise<void> {
    if (storagePaths.length === 0) return;

    const { error } = await supabase.storage.from(this.STORAGE_BUCKET).remove(storagePaths);

    if (error) {
      console.error('[checklistExampleService] could not discard pending uploads', {
        count: storagePaths.length,
        message: error.message,
      });
    }
  }

  /**
   * EXP-2.5 · Save item. Commit the draft.
   *
   * Order matters and each step is here for a reason:
   *
   *   1. MOVE pending files to their final key. A move is an UPDATE on the
   *      storage row, which `storage.protect_delete()` does not touch — the
   *      reason the pending path was chosen over a sweep at all.
   *   2. DELETE rows for this owner that the draft no longer contains, derived
   *      server-side by excluding the ids the draft kept.
   *   3. WRITE the rows, insert or update, with sort_order taken from the
   *      rail's order.
   *   4. REMOVE the files the dropped rows pointed at.
   *
   * ⚠️ STEP 2 RUNS BEFORE STEP 3, AND THE OTHER WAY ROUND LOSES EVERYTHING ON
   * THE FIRST SAVE. A new example has no id, so a first save leaves the kept-id
   * list empty, the exclusion filter becomes a no-op, and a delete-after-write
   * removes exactly the rows just inserted. See the note at step 2.
   *
   * ⚠️ FILES MOVE BEFORE ROWS ARE WRITTEN. If the move fails, no row has been
   * written and the draft is untouched, so the admin can retry. Writing rows
   * first and then failing the move would leave rows pointing at pending keys
   * that a later discard would delete.
   */
  async commitDraft(params: {
    tenantId: string;
    owner: { checklistItemId: number } | { libraryTemplateId: string; libraryItemId: string };
    rows: ExampleWriteRow[];
    filesToDelete: string[];
  }): Promise<void> {
    const { tenantId, owner, rows, filesToDelete } = params;

    // 1 · Move anything still pending.
    const moved: ExampleWriteRow[] = [];
    for (const row of rows) {
      if (!row.wasPending) {
        moved.push(row);
        continue;
      }

      const fileName = row.storagePath.split('/').pop() as string;
      const destination = finalPath(tenantId, owner, fileName);

      const { error } = await supabase.storage
        .from(this.STORAGE_BUCKET)
        .move(row.storagePath, destination);

      if (error) {
        console.error('[checklistExampleService] could not move a pending file', {
          from: row.storagePath,
          to: destination,
          message: error.message,
        });
        throw error;
      }

      moved.push({ ...row, storagePath: destination });
    }

    // 2 · Remove rows this owner no longer has — BEFORE writing, not after.
    //
    // ⚠️ THIS ORDER IS THE WHOLE FIX, AND THE OTHER ORDER DESTROYED EVERY
    // EXAMPLE ON THE VERY FIRST SAVE.
    //
    // Deleting afterwards meant excluding the ids the draft kept. A NEW example
    // has no id yet, so on a first save `keptIds` is EMPTY, the exclusion
    // filter was skipped as a no-op, and the delete became an unqualified
    //
    //     DELETE FROM checklist_item_examples WHERE checklist_item_id = <id>
    //
    // which removed the four rows inserted moments earlier. The files had
    // already been moved to their final keys, so storage kept four orphans and
    // the item showed no examples at all.
    //
    // Deleting first inverts the dependency: only rows the draft still carries
    // an id for survive, and the new ones are written afterwards where nothing
    // can reach back and remove them.
    const keptIds = moved.map((r) => r.id).filter((id): id is string => Boolean(id));

    let deleteQuery = supabase.from('checklist_item_examples').delete();
    deleteQuery =
      'checklistItemId' in owner
        ? deleteQuery.eq('checklist_item_id', owner.checklistItemId)
        : deleteQuery
            .eq('library_template_id', owner.libraryTemplateId)
            .eq('library_item_id', owner.libraryItemId);

    if (keptIds.length > 0) {
      deleteQuery = deleteQuery.not('id', 'in', `(${keptIds.join(',')})`);
    }

    const { error: deleteError } = await deleteQuery;
    if (deleteError) {
      console.error('[checklistExampleService] could not remove dropped examples', {
        message: deleteError.message,
      });
      throw deleteError;
    }

    // 3 · Write the rows.
    const ownerColumns =
      'checklistItemId' in owner
        ? { checklist_item_id: owner.checklistItemId, library_template_id: null, library_item_id: null }
        : {
            checklist_item_id: null,
            library_template_id: owner.libraryTemplateId,
            library_item_id: owner.libraryItemId,
          };

    const payload = moved.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      tenant_id: tenantId,
      ...ownerColumns,
      storage_path: row.storagePath,
      caption: row.caption,
      is_good: row.isGood,
      sort_order: row.sortOrder,
      updated_at: new Date().toISOString(),
    }));

    if (payload.length > 0) {
      const { error } = await supabase
        .from('checklist_item_examples')
        .upsert(payload, { onConflict: 'id' });

      if (error) {
        console.error('[checklistExampleService] could not write examples', {
          count: payload.length,
          message: error.message,
        });
        throw error;
      }
    }

    // 4 · And their files. Logged rather than thrown: the rows are already
    // correct, and failing the save over a leftover object would be worse.
    if (filesToDelete.length > 0) {
      const { error } = await supabase.storage.from(this.STORAGE_BUCKET).remove(filesToDelete);
      if (error) {
        console.error('[checklistExampleService] could not remove dropped files', {
          count: filesToDelete.length,
          message: error.message,
        });
      }
    }
  }

  /**
   * Sign every example's object key.
   *
   * ⚠️ ONE BATCHED CALL, NOT ONE PER ROW. A field can carry a dozen examples
   * and the chip renders three of them before anything is opened; a request
   * per photo is a visible stall on a phone in a driveway.
   *
   * ⚠️ A ROW WHOSE URL WILL NOT SIGN IS DROPPED, NOT RENDERED BROKEN. The
   * alternative is a grid of broken-image icons that looks like the feature is
   * broken rather than like one file is missing.
   */
  private async withSignedUrls(rows: ChecklistItemExample[]): Promise<ExampleWithUrl[]> {
    const paths = rows.map((r) => r.storage_path);

    const { data, error } = await supabase.storage
      .from(this.STORAGE_BUCKET)
      .createSignedUrls(paths, this.SIGNED_URL_TTL_SECONDS);

    if (error || !data) {
      console.error('[checklistExampleService] could not sign example urls', {
        count: paths.length,
        message: error?.message,
      });
      return [];
    }

    const urlByPath = new Map<string, string>();
    data.forEach((entry, index) => {
      // createSignedUrls returns results positionally and reports per-entry
      // failure in `entry.error`, so match on index rather than on `entry.path`,
      // which is not populated consistently across client versions.
      if (entry?.signedUrl && !entry.error) {
        urlByPath.set(paths[index], entry.signedUrl);
      }
    });

    return rows
      .filter((row) => urlByPath.has(row.storage_path))
      .map((row) => ({ ...row, file_url: urlByPath.get(row.storage_path) as string }));
  }
}

export const checklistExampleService = new ChecklistExampleService();
